import { memo, useContext, useEffect, useState } from 'react';
import { KolSearchStatusEnum } from '@trinity/chat';
import type { KolSearchForm } from '@trinity/chat';
import AudioStatusListener from './AudioStatusListener';
import {
  KolSearchStatusContext,
  KolSearchFormContext,
  KolSearchResultsContext,
  KolSearchRecommendationsContext,
  KolSearchIdContext,
  KolSearchTablePageContext,
} from './context';
import { ProfileSelectionErrorMessage, SearchErrorMessage } from './KolSearch.Error';
import { KolSearchFormMessage } from './KolSearch.Form';
import { KolProfileMessageContainer } from './KolSearch.Profile';
import { QuestionsBank } from './KolSearch.QuestionsBank';
import { KolRecommendations } from './KolSearch.Recommendations';
import { KolResultsMessage } from './KolSearch.Results';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { SystemUserPlaceholderMessage } from './Message.System.Placeholder';
import { MessageWrapper } from './Message.System.Wrapper';
import { UserMessage } from './Message.User';

export const KolSearchItem = () => {
  const searchStatus = useContext(KolSearchStatusContext);
  const results = useContext(KolSearchResultsContext);
  const recommendations = useContext(KolSearchRecommendationsContext);
  const kolSearchId = useContext(KolSearchIdContext);
  const searchContext = useContext(KolSearchTablePageContext);

  const showSearchForm = searchStatus == KolSearchStatusEnum.FormEntry;
  const showUserMessage = ![KolSearchStatusEnum.ProfileRecommendations, KolSearchStatusEnum.FormEntry, KolSearchStatusEnum.QuestionsBank].includes(searchStatus);
  const showSearchResultsLoadingMessage = searchStatus === KolSearchStatusEnum.FormSubmitted;

  const [showResultsState, setShowResultsState] = useState<'awaiting-results' | 'show-results' | 'no-results' | 'hidden'>('awaiting-results');

  useEffect(() => {
    if (searchStatus === KolSearchStatusEnum.ProfileSelected) {
      setShowResultsState('hidden');
      return; // Exit early to prevent the second condition from overriding
    }

    if (results) {
      const shouldShowResults = [
        KolSearchStatusEnum.MultipleResultsReturned,
        KolSearchStatusEnum.SingleResultReturned,
        KolSearchStatusEnum.SearchCancelled,
        // KolSearchStatusEnum.ProfileSelected,
        KolSearchStatusEnum.ProfileSelectionSubmitted,
        KolSearchStatusEnum.NoResultsReturned,
        KolSearchStatusEnum.ProfileRecommendations,
      ].includes(searchStatus) && ((results && results.length > 1) || searchContext?.requery || KolSearchStatusEnum.NoResultsReturned);

      if (shouldShowResults) {
        setShowResultsState('show-results');
      } else {
        setShowResultsState('no-results');
      }
    }
  }, [searchStatus, results, searchContext]);

  const showProfile = searchStatus === KolSearchStatusEnum.ProfileSelected;
  const showProfileLoadingMessage = searchStatus === KolSearchStatusEnum.ProfileSelectionSubmitted;
  const showSearchErrorMessage = searchStatus === KolSearchStatusEnum.FormSubmissionErrored;
  const showProfileErrorMessage = searchStatus === KolSearchStatusEnum.ProfileSelectionErrored;
  const showRecommendations = recommendations.status !== 'skipped';
  const showQuestionsBank = searchStatus === KolSearchStatusEnum.QuestionsBank;

  return (
    <>
      {showQuestionsBank && <QuestionsBank />}
      {showRecommendations && <KolRecommendations />}
      {showSearchForm && <KolSearchFormMessage />}
      {showUserMessage && <UserSearchMessage />}
      {showSearchResultsLoadingMessage && <SystemUserPlaceholderMessage />}
      {showResultsState === 'show-results' && <KolResultsMessage />}
      {showResultsState === 'no-results' && <NoResultsMessage />}
      {showProfileLoadingMessage && <SystemUserPlaceholderMessage />}
      {showProfile && (
        <AudioStatusListener
          queryIdentifier={`kol-search:${kolSearchId}`}
          kolSearchId={kolSearchId}>
          <KolProfileMessageContainer />
        </AudioStatusListener>
      )}
      {showSearchErrorMessage && <SearchErrorMessage />}
      {showProfileErrorMessage && <ProfileSelectionErrorMessage />}
    </>
  );
};

const NoResultsMessage = memo(() => {
  const copy = `I'm sorry, I couldn't find any HCPs using the filters you provided. Please try again.`;
  return (
    <MessageWrapper>
      <BasicSystemUserMessage>
        {copy}
      </BasicSystemUserMessage>
    </MessageWrapper>
  );
});

const UserSearchMessage = memo(() => {
  const formState = useContext(KolSearchFormContext);
  const copy = formState.kolId
    ? `Show me the profile for "${formState.name}".`
    : buildSearchMessage(formState);
  return (
    <UserMessage value={copy} />
  );
});

function buildSearchMessage(formState: Partial<KolSearchForm>) {
  const filters: string[] = [];

  if (formState.name) {
    filters.push(`with the name "${formState.name}"`);
  }

  if (formState.location) {
    if (formState.location.name) {
      filters.push(`near the location "${formState.location.name}"`);
    } else {
      filters.push(`near the location (${formState.location.latitude}, ${formState.location.longitude})`);
    }
  }

  return `Find any matching HCPs ${filters.join(' and ')}.`;
}